'use client'

import * as React from 'react'
import { Drawer as DrawerPrimitive } from 'vaul'

import { cn } from '@/lib/utils'
import { Button } from '../Button'

type DrawerContextType = {
  direction: 'left' | 'right' | 'top' | 'bottom'
}

type DrawerProps = React.ComponentProps<typeof DrawerPrimitive.Root> & {
  direction?: DrawerContextType['direction']
}

type DrawerContentProps = React.ComponentProps<
  typeof DrawerPrimitive.Content
> & {
  horizontalDirectionWidth?: number
  zIndex?: number
}

const DrawerContext = React.createContext<DrawerContextType>({
  direction: 'right',
})

const Drawer = ({
  shouldScaleBackground = true,
  direction = 'right',
  ...props
}: DrawerProps) => (
  <DrawerContext.Provider value={{ direction }}>
    <DrawerPrimitive.Root
      shouldScaleBackground={shouldScaleBackground}
      direction={direction}
      {...props}
    />
  </DrawerContext.Provider>
)

const DrawerTrigger = DrawerPrimitive.Trigger

const DrawerPortal = DrawerPrimitive.Portal

const DrawerClose = DrawerPrimitive.Close

const DrawerOverlay = ({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Overlay>) => (
  <DrawerPrimitive.Overlay
    data-test="drawer-overlay"
    ref={ref}
    className={cn('fixed inset-0 z-2001 bg-black/80', className)}
    {...props}
  />
)

const DrawerContent = ({
  ref,
  className,
  children,
  horizontalDirectionWidth = 800,
  zIndex,
  ...props
}: DrawerContentProps) => {
  const { direction } = React.useContext(DrawerContext)

  const isHorizontalDirection = direction === 'left' || direction === 'right'
  const isVerticalDirection = direction === 'top' || direction === 'bottom'

  const horizontalDirectionDrawerStyle = {
    width: `${horizontalDirectionWidth}px`,
    maxWidth: '95%',
  }

  return (
    <DrawerPortal>
      <DrawerOverlay style={{ zIndex }} />
      <DrawerPrimitive.Content
        ref={ref}
        style={{
          ...(isHorizontalDirection && horizontalDirectionDrawerStyle),
          zIndex: zIndex ? zIndex + 1 : 2001,
        }}
        className={cn(
          'fixed bottom-0 border bg-background',
          isVerticalDirection &&
            'inset-x-0 z-50 mt-24 flex h-auto flex-col rounded-t-[10px]',
          isHorizontalDirection &&
            'h-dvh overflow-x-hidden overflow-y-auto pt-8 pb-5',
          direction === 'left' && 'left-0 rounded-r-[10px]',
          direction === 'right' && 'right-0 rounded-l-[10px]',
          className,
        )}
        {...props}
      >
        {/* Horizontal Drawers will have an X to close */}
        {isHorizontalDirection && (
          <DrawerClose
            style={{ zIndex: 1101 }}
            className="absolute top-5 right-5"
          >
            <Button variant="icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="size-4"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </Button>
          </DrawerClose>
        )}

        {/* Vertical Drawers will have a line handlebar to indicate swipe to close */}
        {isVerticalDirection && (
          <div className="mx-auto mt-4 h-2 w-[100px] rounded-full bg-muted" />
        )}

        {children}
      </DrawerPrimitive.Content>
    </DrawerPortal>
  )
}

const DrawerHeader = ({ className, ...props }: React.ComponentProps<'div'>) => (
  <div
    className={cn('grid gap-1.5 p-4 text-center sm:text-left', className)}
    {...props}
  />
)

const DrawerFooter = ({ className, ...props }: React.ComponentProps<'div'>) => (
  <div
    className={cn('mt-auto flex flex-col gap-2 p-4', className)}
    {...props}
  />
)

const DrawerTitle = ({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Title>) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg leading-none font-semibold tracking-tight',
      className,
    )}
    {...props}
  />
)

const DrawerDescription = ({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Description>) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
)

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
}
